import { useEffect } from 'react';

import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import useBreakpoints from '#client/hooks/useBreakpoints';
import useToastContext from '#client/hooks/useToastContext';

import ConfirmModalDesktopView from './components/ConfirmModalDesktopView';
import ConfirmModalMobileView from './components/ConfirmModalMobileView';
import { ContentStyled } from './ConfirmModal.styles';

export default function ConfirmModal({
  title,
  message,
  okText = 'OK',
  imagePath = null,
  cancelText = 'Cancel',
  handleOK,
  handleClose,
  handleCancel,
  loading = false
}) {
  const { clearAllToast } = useToastContext();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const onClickOK = async () => {
    if (handleOK?.constructor?.name === 'AsyncFunction') {
      await handleOK?.();
    } else {
      handleOK?.();
    }
    handleClose();
  };

  const onClickCancel = () => {
    handleCancel?.();
    handleClose();
  };

  const content = (
    <Stack spacing="24px">
      <Heading
        align="center"
        size="2xl"
        weight="bold"
      >
        {title}
      </Heading>
      <Text
        align="center"
        size="xl"
      >
        {message}
      </Text>
      {imagePath && (
        <Box>
          <SharedAsset
            alt=""
            src={imagePath}
          />
        </Box>
      )}
      <Stack spacing="4px">
        <LoadingButton
          color="error"
          disabled={loading}
          fullWidth
          loading={loading}
          onClick={onClickOK}
          size="large"
          variant="contained"
        >
          {okText}
        </LoadingButton>
        <LoadingButton
          color="primary"
          fullWidth
          onClick={onClickCancel}
          size="large"
          variant="outlined"
        >
          {cancelText}
        </LoadingButton>
      </Stack>
    </Stack>
  );

  useEffect(() => {
    clearAllToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isDesktop && (
        <ConfirmModalMobileView handleClose={handleClose}>
          <ContentStyled>{content}</ContentStyled>
        </ConfirmModalMobileView>
      )}
      {isDesktop && (
        <ConfirmModalDesktopView handleClose={handleClose}>{content}</ConfirmModalDesktopView>
      )}
    </>
  );
}

ConfirmModal.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  okText: PropTypes.string,
  cancelText: PropTypes.string,
  handleOK: PropTypes.func,
  handleCancel: PropTypes.func,
  handleClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  imagePath: PropTypes.string
};
